import { useEffect, useMemo } from 'react';
import { useSplitClient } from '@splitsoftware/splitio-react';

import { trackExperimentAssignment } from 'utils/analyticsUtils';
import { getAnonymousId } from 'utils/globalUtils';
import { getIsDatadogSyntheticTest } from 'utils/signupUtils';

export const FLAG_NAME = 'dietary-preferences-in-signup';

/**
 * Split treatment values for the "Dietary Preferences in Signup" experiment.
 */
export const SplitTreatment = Object.freeze({
    STATIC: 'static',
    DYNAMIC: 'dynamic',
    OFF: 'off',
});

/**
 * Returns the Split treatment for the "Dietary Preferences in Signup" feature flag.
 *
 * @returns {Object} The user's Split data.
 */
export function useDietaryPreferencesFeatureFlag() {
    let treatmentAttributes = {};

    const isDatadogSyntheticTest = useMemo(() => getIsDatadogSyntheticTest(), []);

    if (isDatadogSyntheticTest) {
        treatmentAttributes = { utm_source: 'datadog' };
    }

    const {
        client: splitClient,
        isReady: splitIsReady,
        isTimedout: splitTimedout,
    } = useSplitClient({ splitKey: getAnonymousId() });

    const splitTreatment = splitClient?.getTreatments([FLAG_NAME], treatmentAttributes)?.[FLAG_NAME];

    useEffect(() => {
        if (splitIsReady && !splitTimedout) {
            trackExperimentAssignment({
                experiment: FLAG_NAME,
                variation: splitTreatment,
            });
        }
    }, [splitIsReady, splitTimedout]);

    return {
        isOnTreatment: splitTreatment === 'static' || splitTreatment === 'dynamic',
        isReady: splitIsReady,
        key: splitClient?.key,
        treatment: splitTreatment,
    };
}
